import { createContext, useContext, type ReactNode } from 'react'
import { useTrailRecorder } from '../hooks/useTrailRecorder'
import { useScreenWakeLock } from '@hooks/useScreenWakeLock'

/**
 * App-level owner of the trail recorder. By hosting useTrailRecorder here —
 * above React Router — the geolocation watch and its in-progress track survive
 * in-app navigation; the watch is only torn down when the whole protected
 * layout unmounts (e.g. logout). TrailRecorderPage and the FloatingRecorderBar
 * both read the same live recording state through useRecorder().
 */

type RecorderContextValue = ReturnType<typeof useTrailRecorder>

const RecorderContext = createContext<RecorderContextValue | null>(null)

export function RecorderProvider({ children }: { children: ReactNode }) {
  const recorder = useTrailRecorder()
  // Keep the screen awake while actively recording so display sleep / device
  // lock doesn't throttle geolocation and stall the track. Released on
  // pause/stop and re-acquired when the tab returns to the foreground.
  useScreenWakeLock(recorder.status === 'recording')
  return <RecorderContext.Provider value={recorder}>{children}</RecorderContext.Provider>
}

/** Access the shared recorder state/actions. Must be used inside RecorderProvider. */
export function useRecorder(): RecorderContextValue {
  const ctx = useContext(RecorderContext)
  if (!ctx) {
    throw new Error('useRecorder must be used within a RecorderProvider')
  }
  return ctx
}
